import React, { useMemo } from 'react';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { Label } from '@/components/ui/label';
import { MultiSelectDropdownWithField, MultiSelectOption } from '@/components/ui/dropdowns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { Lock, LockOpen } from 'lucide-react';

interface OwnerAndPermissionsProps {
  owner: string | null;
  editPermissions: 'Open' | 'Locked';
  onOwnerChange: (owner: string | null) => void;
  onPermissionsChange: (permissions: 'Open' | 'Locked') => void;
  disabled?: boolean;
  selectedGameId?: string;
}

export function OwnerAndPermissions({
  owner,
  editPermissions,
  onOwnerChange,
  onPermissionsChange,
  disabled = false,
  selectedGameId,
}: OwnerAndPermissionsProps) {
  const { getGameMembers } = useSimplifiedUserData();

  // Get game members for owner dropdown
  const userOptions: MultiSelectOption[] = useMemo(() => {
    if (!selectedGameId) return [];
    const members = getGameMembers(selectedGameId);
    return members.map(member => ({
      id: member.id,
      label: member.username || member.email,
      description: member.email !== member.username ? member.email : undefined,
    }));
  }, [selectedGameId, getGameMembers]);

  return (
    <div className="flex flex-wrap gap-3 items-start pt-2">
      <div className="flex-[2] min-w-[200px] relative z-[100]">
        <Label htmlFor="basic-info-owner" className="text-sm">Owner</Label>
        <MultiSelectDropdownWithField
          value={owner || ''}
          onChange={(value) => onOwnerChange(value as string || null)}
          options={userOptions}
          placeholder="Select owner (optional)"
          multiple={false}
          disabled={disabled}
          className="mt-1"
          showSelectedOptions={true}
        />
      </div>
      <div className="flex-1 min-w-[140px] relative z-[100]">
        <Label htmlFor="basic-info-permissions" className="text-sm">Edit Permissions</Label>
        <Select
          value={editPermissions}
          onValueChange={(value) => onPermissionsChange(value as 'Open' | 'Locked')}
          disabled={disabled}
        >
          <SelectTrigger className="mt-1 border border-border rounded p-2 w-full">
            <SelectValue placeholder="Select permissions" />
          </SelectTrigger>
          <SelectContent className="z-[100]" position="popper" side="top" sideOffset={4}>
            <SelectItem value="Open">
              <div className="flex items-center gap-2 ">
                <LockOpen className="h-4 w-4" />
                <span>Open</span>
              </div>
            </SelectItem>
            <SelectItem value="Locked">
              <div className="flex items-center gap-2 ">
                <Lock className="h-4 w-4" />
                <span>Locked</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
